import { Link } from 'react-router-dom'
import { Check, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/cn'

export type SetupStep = {
  title: string
  description: string
  to: string
  done: boolean
  cta: string
}

type SetupChecklistProps = {
  title: string
  description?: string
  steps: SetupStep[]
}

export function SetupChecklist({ title, description, steps }: SetupChecklistProps) {
  const completed = steps.filter((s) => s.done).length
  if (completed === steps.length) return null
  const nextIndex = steps.findIndex((s) => !s.done)

  return (
    <section className="rounded-xl border border-border bg-card p-6">
      <div className="mb-4">
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">
          {description || `${completed} of ${steps.length} steps complete`}
        </p>
      </div>
      <ol className="flex flex-col gap-3">
        {steps.map((step, i) => {
          const isNext = i === nextIndex
          return (
            <li
              key={step.title}
              className={cn(
                'flex items-center gap-4 rounded-lg border border-border p-3',
                isNext && 'border-primary bg-accent',
              )}
            >
              <span
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
                  step.done ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
                )}
              >
                {step.done ? <Check className="size-4" /> : i + 1}
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className={cn('text-sm font-medium', step.done && 'text-muted-foreground line-through')}>
                  {step.title}
                </span>
                <span className="text-xs text-muted-foreground">{step.description}</span>
              </div>
              {!step.done && (
                <Link
                  to={step.to}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary-hover"
                >
                  {step.cta}
                  <ArrowRight className="size-3" />
                </Link>
              )}
            </li>
          )
        })}
      </ol>
    </section>
  )
}
